import axios from 'axios';
import { REACT_APP_TEAMCAMP_APIURL } from '../global/environment';
import { getAPIErrorReason, isDev, isEmpty } from '../helpers/common';
import UserPreferenceSingleton from '../helpers/userPreferenceSingleton';
import { CustomerListInterface, MemberInputInterface } from '../interfaces/CustomerInterface';
import { captureException } from './logService';
import { rootStore } from '../mobx/rootStore';

interface UpdateCustomerPayload {
  Address?: string;
  CompanyName?: string;
  Email?: string;
  Phone?: string | number;
  IsArchived?: boolean;
  ProfileImage?: string;
}

/**
 * @desc Customer - Get customer list
 * @param {*}
 */
export const getCustomerList = async () => {
  try {
    rootStore.customerStore.setLoading(true);
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/customer/getCustomerList`);
    const { data } = response;
    if (data?.length > 0) {
      const updatelist = data?.map((item: CustomerListInterface) => {
        return {
          id: item['_id'],
          phone: item?.Phone,
          firstName: item?.FirstName,
          lastName: item?.LastName,
          address: item?.Address,
          email: item?.Email,
          companyName: item?.CompanyName,
          companyId: item?.CompanyId,
          key: item['_id'],
          value: item?.CompanyName,
          profileImage: item?.ProfileImage
        };
      });
      rootStore.customerStore.setCustomers(updatelist);
      return updatelist;
    } else {
      rootStore.customerStore.setCustomers([]);
      return undefined;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get customer list please try again';
    captureException(error);
    dispatchCustomerError(error);
    return undefined;
  } finally {
    rootStore.customerStore.setLoading(false);
  }
};

/**
 * @desc Customer - create new customer
 * @param {*}
 */
export const createNewCustomer = async (payload: { CompanyName: string; Address: string }) => {
  try {
    rootStore.customerStore.setLoading(true);
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/customer/addCustomer`, payload);
    const { data } = response;
    if (data) {
      return data;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to create customer please try again';
    captureException(error);
    dispatchCustomerError(error);
    return undefined;
  } finally {
    rootStore.customerStore.setLoading(false);
  }
};

/**
 * @desc Customer - update Customer Data
 * @param {*}
 */
export const updateCustomerData = async (customer_id: string, payload: UpdateCustomerPayload): Promise<boolean> => {
  try {
    rootStore.customerStore.setLoading(true);
    const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/customer/putCustomer/${customer_id}`, payload);
    if (response) {
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to update Customer Data please try again';
    captureException(error);
    dispatchCustomerError(error);
    return false;
  } finally {
    rootStore.customerStore.setLoading(false);
  }
};

/**
 * @desc Customer - get individual customer detail
 * @param {*}
 */
export const getIndividualCustomerDetail = async (customer_id: string) => {
  try {
    if (!customer_id) return;
    rootStore.customerStore.setLoading(true);
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/customer/getCustomerDetails/${customer_id}`);
    const { data } = response;
    if (data) {
      const updatelist = {
        id: data['_id'],
        phone: data?.Phone,
        firstName: data?.FirstName,
        lastName: data?.LastName,
        address: data?.Address,
        email: data?.Email,
        companyName: data?.CompanyName,
        companyId: data?.CompanyId,
        profileImage: data?.ProfileImage
      };
      rootStore.customerStore.setIndividualCustomerDetail(updatelist);
      rootStore.invoiceStore.updateInvoiceDetail('customer', updatelist);
      rootStore.invoiceStore.updateInvoiceDetail('companyId', updatelist?.companyId);
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get customer details please try again';
    captureException(error);
    dispatchCustomerError(error);
    return false;
  } finally {
    rootStore.customerStore.setLoading(false);
  }
};

/**
 * @desc Customer - get individual customer projects
 * @param {*}
 */
export const getCustomerProjectDetail = async (workspace_id: string, customer_id: string) => {
  try {
    rootStore.customerStore.setLoading(true);
    const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
    const params = {
      customer_id,
      company_id: workspace_id,
      user_id: userDetails?.id
    };
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/project/getProjectIdNameList`, { params });
    const { data } = response;
    if (data) {
      const updatedData = data?.map((item: { id: string; name: string }) => {
        return { ...item, key: item?.id, value: item?.name };
      });
      return updatedData;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get Customer Project Details please try again';
    captureException(error);
    dispatchCustomerError(error);
    return undefined;
  } finally {
    rootStore.customerStore.setLoading(false);
  }
};

/**
 * @desc Customer - add member
 * @param {*}
 */
export const addCustomerMember = async (payload: MemberInputInterface, customerId: string) => {
  try {
    if (isEmpty(payload?.name)) {
      dispatchCustomerError('Please enter name of member');
      return false;
    } else if (isEmpty(payload?.email)) {
      dispatchCustomerError('Please enter email of member');
      return false;
    } else if (isEmpty(payload?.role)) {
      dispatchCustomerError('Please select role for member');
      return false;
    }

    rootStore.customerStore.setLoading(true);
    const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
    const updatedPaylod = { ...payload, customerId, userId: userDetails?.id, isDev: isDev() };
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/customer/addCustomerMember`, updatedPaylod);
    const { data } = response;
    if (data) {
      dispatchCustomerSuccess(data?.message);
      return data?.data;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to add member please try again';
    captureException(error);
    dispatchCustomerError(error);
    return undefined;
  } finally {
    rootStore.customerStore.setLoading(false);
  }
};

/**
 * @desc Customer - update member
 * @param {*}
 */
export const updateCustomerMember = async (payload: MemberInputInterface, customerId: string) => {
  try {
    if (isEmpty(payload?.name)) {
      dispatchCustomerError('Please enter name of member');
      return false;
    } else if (isEmpty(payload?.email)) {
      dispatchCustomerError('Please enter email of member');
      return false;
    } else if (isEmpty(payload?.role)) {
      dispatchCustomerError('Please select role for member');
      return false;
    }

    rootStore.customerStore.setLoading(true);
    const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
    const updatedPaylod = { ...payload, customerId, userId: userDetails?.id, memberId: payload?.id, isDev: isDev() };
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/customer/putCustomerMemberDetails`, updatedPaylod);
    const { data } = response;
    if (data) {
      dispatchCustomerSuccess(data?.message);
      return data?.data;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to update member please try again';
    captureException(error);
    dispatchCustomerError(error);
    return undefined;
  } finally {
    rootStore.customerStore.setLoading(false);
  }
};

/**
 * @desc Customer - get members list
 * @param {*}
 */
export const getCustomerMembersList = async (customerId: string) => {
  try {
    rootStore.customerStore.setLoading(true);
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/customer/getCustomerMembersList/${customerId}`);
    const { data } = response;
    if (data) {
      rootStore.customerStore.setCustomerMembers(data?.data || []);
      return data?.data || [];
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get member list please try again';
    captureException(error);
    dispatchCustomerError(error);
    return undefined;
  } finally {
    rootStore.customerStore.setLoading(false);
  }
};

/**
 * @desc Customer - delete customer member
 * @param {*}
 */
export const deleteCustomerMember = async (member_id: string) => {
  try {
    rootStore.customerStore.setLoading(true);
    const currentUser = UserPreferenceSingleton.getInstance().getCurrentUser();
    const response = await axios.delete(`${REACT_APP_TEAMCAMP_APIURL}/customer/deleteCustomerMember`, {
      params: { memberId: member_id, userId: currentUser?.id }
    });
    const { data } = response;
    if (data) {
      dispatchCustomerSuccess(data?.message);
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to delete member please try again';
    captureException(error);
    dispatchCustomerError(error);
    return false;
  } finally {
    rootStore.customerStore.setLoading(false);
  }
};

/**
 * @desc Customer - get invoice list
 * @param {*}
 */
export const getCustomersInvoiceList = async (customerId: string) => {
  try {
    rootStore.customerStore.setLoading(true);
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/invoice/getInvoiceListByCustomer/${customerId}`);
    const { data } = response;
    if (data) {
      return data;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get Customers Invoice List please try again';
    captureException(error);
    dispatchCustomerError(error);
    return undefined;
  } finally {
    rootStore.customerStore.setLoading(false);
  }
};

/**
 * @desc Customer - get Customers Project List
 * @param {*}
 */
export const getCustomersProjectList = async (customer_id: string) => {
  try {
    rootStore.customerStore.setLoading(true);
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/project/getCustomersProjectList/${customer_id}`);
    const { data } = response;
    if (data) {
      return data;
    } else {
      return [];
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get customer projects please try again';
    captureException(error);
    dispatchCustomerError(error);
    return [];
  } finally {
    rootStore.customerStore.setLoading(false);
  }
};

export function dispatchCustomerError(msg: string) {
  rootStore.alertNotificationStore.setErrorMessage(msg);
}

export function dispatchCustomerSuccess(msg: string) {
  rootStore.alertNotificationStore.setSuccessMessage(msg);
}
